// internal imports
import React, { useEffect, useMemo, useState } from "react";
import {
  useDocumentTitle,
  useGetService,
  usePostService,
} from "../../../hooks";
import { getUserData, serviceRequestTypeToLabelMap } from "../../../shared/utils";

// Third party
import _ from "lodash";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

// style
import "../Assignment.scss";
import { AssignmentToolbar } from "./AssignmentToolbar";
import { API } from "../../../constants";
import { removeSecondsFromDateTime, addDays, differenceInDays } from "../../../utils/date.utils";
import moment from "moment";
import { EServiceRequestType } from "../../../types/global.types";
import { Typography, Box } from "@mui/material";
import { MGDatePicker } from "../../../components";

export const Upcoming = () => {
  useDocumentTitle("Upcoming");
  const { postData, isSubmitting } = usePostService();
  //Filter set
  const [searchParams, setSearchParams] = useState("");
  const [selectedHotel, setSelectedHotel] = useState("");
  const [selectedFlight, setSelectedFlight] = useState("");
  const [selectedShip, setSelectedShip] = useState("");
  const [selectedForUnAssign, setSelectedForUnAssign] = useState([]);

  const [startDate, setStartDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState<Date>(() => addDays(new Date(), 3));

  const vendorId = getUserData()["cognito:username"];

  const formattedStartDate = moment(startDate).format("DD/MM/YYYY");
  const formattedEndDate = moment(endDate).format("DD/MM/YYYY");

  const apiUrl = useMemo(
    () =>
      `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    [vendorId, formattedStartDate, formattedEndDate]
  );

  const {
    response,
    isLoading,
    refetch: refetchAssinged,
  } = useGetService(
    apiUrl
  );

  const rows = useMemo(() => {
    return response?.data || [];
  }, [response]);

  const handleSelectAirline = (data: any) => {
    setSelectedFlight(data ? data.value : "");
  };

  const handleSelectShip = (data: any) => {
    setSelectedShip(data ? data.value : "");
  };

  const handleSelectHotel = (data: any) => {
    setSelectedHotel(data ? data.value : "");
  };

  const handleSearchParam = (text: string) => {
    setSearchParams(text);
  };

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    const maxEndDate = addDays(date, 10);
    if (endDate > maxEndDate) {
      setEndDate(maxEndDate);
    }
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
    const minStartDate = addDays(date, -10);
    if (startDate < minStartDate) {
      setStartDate(minStartDate);
    }
  };

  const columnHeaderData = useMemo(() => {
    return [
      {
        field: "tmId",
        headerName: "Crew Id",
        flex: 1,
      },
      {
        field: "FULL_NAME",
        headerName: "Full name",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return `${row.FirstName || ""} ${row.LastName || ""}` || "";
        },
      },
      {
        field: "arrivalDate",
        headerName: "Arrival/Checkout date",
        flex: 1.5,
        valueGetter: (value: any, row: any) => {
          return removeSecondsFromDateTime(`${row.arrivalDate || ""} ${ row.arrivalTime || ""}` ||  "");
        },
      },
      {
        field: "empName",
        headerName: "Assigned name",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return row.empName;
        },
      },
      {
        field: "HotelName",
        headerName: "Hotel",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return row.HotelName;
        },
      },
      {
        field: "Airline",
        headerName: "Flight",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          const airline = row?.OperatingAirlineName;
            const flight = row?.FlightNbr;

            if (!airline || !flight) {
              return "";
            }

            return `${airline} - ${flight}`.trim();
        },
        renderCell: (params: any) => {
          const airline = params?.row?.OperatingAirlineName;
          const flight = params?.row?.FlightNbr;

          if (!airline || !flight) {
            return <></>;
          }

          return <>{`${airline} - ${flight}`}</>;
        },
        exportValueGetter: (params: any) => {
          const airline = params?.row?.OperatingAirlineName;
            const flight = params?.row?.FlightNbr;
            return airline && flight ? `${airline} - ${flight}` : "";
        },
      },
      // {
      //   field: "isUnManifested",
      //   headerName: "Unlisted TM",
      //   flex: 1,
      //   valueGetter: (value: any, row: any) => {
      //     return value === true ? "Yes" : "No";
      //   },
      // },
      {
        field: "status",
        headerName: "Scanned",
        flex: 1,
        valueFormatter: (value: any) => {
          return value === "pending" ? "No" : "Yes";
        },
        renderCell: (params: any) => (
          <>
            {params.value !== "pending" ? (
              <CheckCircleIcon
                style={{
                  color: "green",
                  paddingLeft: "20px",
                  paddingTop: "13px",
                }}
              />
            ) : (
              <CancelIcon
                style={{
                  color: "grey",
                  paddingLeft: "20px",
                  paddingTop: "13px",
                }}
              />
            )}
          </>
        ),
      },
      {
        field: "requestType",
        headerName: "Request type",
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return  serviceRequestTypeToLabelMap[row.requestType as EServiceRequestType] || row.requestType;
        },
      },
    ];
  }, []);

  const rowData = useMemo(() => {
    let data = [...rows];
    if (searchParams || selectedShip || selectedFlight || selectedHotel) {
      const searchParamsLowerCase = searchParams.toLowerCase();
      data = rows?.filter((item: any) => {
        return (
          (item.EmpId?.includes(searchParamsLowerCase) ||
          (item.FirstName?.toLowerCase() || "")?.includes(searchParamsLowerCase) ||
          (item.LastName?.toLowerCase() || "")?.includes(searchParamsLowerCase) || 
          (item.empName?.toLowerCase() || "")?.includes(searchParamsLowerCase) 
        ) &&
          (item?.FutureAssignments_ShipName?.toLowerCase() || "")?.includes(selectedShip?.toLowerCase()) &&
          (item.OperatingAirlineName?.toLowerCase() || "")?.includes(selectedFlight?.toLowerCase()) &&
          (item.HotelName?.toLowerCase() || "")?.includes(selectedHotel?.toLowerCase()) 
        );
      });
    }
    return data;
  }, [searchParams, rows, selectedFlight, selectedShip, selectedHotel]);

  const flightsOptions = useMemo(() => {
    const data = rows.map((x: any) => {
      return {
        value: x.OperatingAirlineName || "",
        label: `${x.OperatingAirlineName || ""} ${x.OperatingAirline || ""}`,
      };
    });
    return _.uniqBy(data, "value");
  }, [rows]);

  const hotelOptions = useMemo(() => {
    const data = rows.map((x: any) => {
      return { value: x.HotelName || "", label: x.HotelName || "" };
    });
    return _.uniqBy(data, "value");
  }, [rows]);

  const shipOptions = useMemo(() => {
    const data = rows.map((x: any) => {
      return {
        value: x?.FutureAssignments_ShipName || "",
        label: x?.FutureAssignments_ShipName || "",
      };
    });
    return _.uniqBy(data, "value");
  }, [rows]);

  const handleRowSelectionModelChange = (data: any) => {
    setSelectedForUnAssign(data);
  };

  const handleUnAssign = async () => {
    try {
      const response = await postData(
        `${API.CARNIVAL_ADMIN.REMOVE_SERVICE_REQUEST}`,
        selectedForUnAssign
      );
      toast.success("successfully unassigned !");
      refetchAssinged(apiUrl);
    } catch (err) {
      console.error(err);
      toast.error(`un assigned failed!`);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleStartDatePickerChange = (date: Date | null) => {
    if (date) {
      const validDate = date < today ? today : date;
      handleStartDateChange(validDate);
    }
  };

  const handleEndDatePickerChange = (date: Date | null) => {
    if (date) {
      const validDate = date < today ? today : date;
      handleEndDateChange(validDate);
    }
  };

  return (
    <div className="grid-section">
      <Box display="flex" alignItems="center" gap={8} mb={2}>
        <MGDatePicker
          containerClassName="flex-row"
          label="Start Date"
          dateFormat="MM/dd/yyyy"
          onChange={handleStartDatePickerChange}
          selected={startDate}
          minDate={today}
          maxDate={endDate}
          filterDate={(date) => {
            if (date < today) return false;
            if (endDate) {
              const daysDiff = differenceInDays(endDate, date);
              if (daysDiff > 10 || daysDiff < 0) return false;
            }
            return true;
          }}
        />
        <MGDatePicker
          containerClassName="flex-row"
          label="End Date"
          dateFormat="MM/dd/yyyy"
          onChange={handleEndDatePickerChange}
          selected={endDate}
          minDate={startDate || today}
          filterDate={(date) => {
            if (date < today) return false;
            if (startDate) {
              const daysDiff = differenceInDays(date, startDate);
              if (daysDiff > 10 || daysDiff < 0) return false;
            }
            return true;
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {rowData.length === rows.length
            ? `${rowData.length} assignments`
            : `${rowData.length} of ${rows.length} assignments`}
        </Typography>
      </Box>
      <DataGrid
        rows={rowData || []}
        sx={{
          minHeight: 400,
          height: rowData.length === 0 ? 400 : "auto",
          marginTop: 2,
        }}
        columns={columnHeaderData}
        hideFooter={true}
        loading={isLoading || isSubmitting}
        checkboxSelection
        onRowSelectionModelChange={handleRowSelectionModelChange}
        slots={{
          toolbar: AssignmentToolbar,
        }}
        slotProps={
          {
            toolbar: {
              searchParams,
              handleSearchParam,
              flightsOptions,
              handleSelectAirline,
              hotelOptions,
              handleSelectHotel,
              shipOptions,
              handleSelectShip,
              handleUnAssign,
              isUnassignEnable: selectedForUnAssign.length === 0,
              exportOptions: {
                fileName: "Vendor Staff Assignment Report",
              },
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
          } as any
        }
        getRowClassName={(params) =>
          params.row.cancelled ? "cancelled-row" : ""
        }
      />
    </div>
  );
};
